/**
 * Chrome specific to the scatter and bubble chart: the per-point tooltip, the
 * point data table, and the bubble size key.
 *
 * The group legend is the shared cartesian one with a dot swatch, so it is
 * re-exported here rather than rewritten. Everything else differs: a scatter
 * tooltip reads out one mark rather than a column of series, and its table has
 * a row per point with both measures instead of a row per category.
 */

import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../ui/utils";
import { diamondPoints, markExtent, type MarkShape, type ScatterPoint } from "./scatterUtils";

export { SeriesLegend } from "../cartesianChrome";

/* ------------------------------------------------------------------ */
/* Tooltip                                                             */
/* ------------------------------------------------------------------ */

interface PointTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: ScatterPoint }>;
  xLabel: string;
  yLabel: string;
  zLabel?: string;
  unit: string;
  xFormatter: (value: number) => string;
  yFormatter: (value: number) => string;
  zFormatter: (value: number) => string;
  showGroup: boolean;
  groupLabel?: string;
  markShape?: MarkShape;
}

/**
 * Readout for a single mark. Both measures are named, because on a scatter the
 * axes are the only thing saying what the position means, and a reader who is
 * hovering has already stopped looking at them.
 */
export function PointTooltip({
  active,
  payload,
  xLabel,
  yLabel,
  zLabel,
  unit,
  xFormatter,
  yFormatter,
  zFormatter,
  showGroup,
  groupLabel = "Group",
  markShape = "circle",
}: PointTooltipProps) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  return (
    <div className="max-w-[240px] rounded-xl border border-border bg-card px-3 py-2 shadow-lg">
      <div className="flex items-start gap-2">
        <span
          aria-hidden="true"
          className={
            markShape === "diamond"
              ? "mt-[4px] size-2.5 shrink-0 rotate-45 rounded-[2px]"
              : "mt-[4px] size-2.5 shrink-0 rounded-full"
          }
          style={{ backgroundColor: point.color }}
        />
        <span className="min-w-0 break-words text-xs font-bold leading-snug text-foreground">
          {point.label}
        </span>
      </div>

      <dl className="mt-1.5 space-y-0.5 pl-[18px]">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] font-medium text-muted-foreground">{xLabel}</dt>
          <dd className="text-[11px] font-bold tabular-nums text-foreground">
            {xFormatter(point.x)}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[11px] font-medium text-muted-foreground">{yLabel}</dt>
          <dd className="text-[11px] font-bold tabular-nums text-foreground">
            {yFormatter(point.y)}
            {unit}
          </dd>
        </div>
        {zLabel && typeof point.z === "number" && (
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-[11px] font-medium text-muted-foreground">{zLabel}</dt>
            <dd className="text-[11px] font-bold tabular-nums text-foreground">
              {zFormatter(point.z)}
            </dd>
          </div>
        )}
        {showGroup && point.groupLabel && (
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-[11px] font-medium text-muted-foreground">{groupLabel}</dt>
            <dd className="break-words text-[11px] font-bold text-foreground">
              {point.groupLabel}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bubble size key                                                     */
/* ------------------------------------------------------------------ */

interface SizeKeyProps {
  zLabel: string;
  zRange: [number, number];
  radiusRange: [number, number];
  formatter: (value: number) => string;
  /** Must match the plot's marks, or the key describes a chart nobody is looking at. */
  markShape?: MarkShape;
}

/**
 * Legend for the area encoding.
 *
 * Without it, mark size is an unlabelled third dimension and a reader can only
 * guess what "bigger" is worth. The two marks are drawn in the plot's own shape
 * at the real extents the scale produces, so the key is a sample of the chart
 * rather than a decoration — which is also why `markShape` is not optional in
 * practice: a circular key beside a diamond plot misstates both the silhouette
 * and, because diamonds are area-matched to a wider extent, the size.
 */
export function BubbleSizeKey({
  zLabel,
  zRange,
  radiusRange,
  formatter,
  markShape = "circle",
}: SizeKeyProps) {
  const [zMin, zMax] = zRange;
  const [rMin, rMax] = radiusRange;
  if (!(zMax > zMin)) return null;

  // Sized from the painted extent, not the radius: a diamond reaches further
  // than its circle-equivalent r, and measuring the box from r would clip the
  // larger mark's top and bottom tips.
  const eMin = markExtent(rMin, markShape);
  const eMax = markExtent(rMax, markShape);

  // Both marks in one SVG sharing a baseline. Two separate SVGs drift apart
  // by a pixel or two, which reads as sloppiness on a key this small.
  const gap = 6;
  const width = eMin * 2 + gap + eMax * 2 + 2;
  const height = eMax * 2 + 2;
  const baseline = height - 1;

  const cxMin = eMin + 1;
  const cxMax = eMin * 2 + gap + eMax + 1;
  const swatch = "fill-transparent stroke-muted-foreground";

  return (
    <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-1">
      <span className="text-[11px] font-medium text-muted-foreground">{zLabel}</span>
      <svg width={width} height={height} aria-hidden="true" className="shrink-0">
        {markShape === "diamond" ? (
          <>
            <polygon
              points={diamondPoints(cxMin, baseline - eMin, eMin)}
              className={swatch}
              strokeWidth={1.5}
            />
            <polygon
              points={diamondPoints(cxMax, baseline - eMax, eMax)}
              className={swatch}
              strokeWidth={1.5}
            />
          </>
        ) : (
          <>
            <circle cx={cxMin} cy={baseline - eMin} r={eMin} className={swatch} strokeWidth={1.5} />
            <circle cx={cxMax} cy={baseline - eMax} r={eMax} className={swatch} strokeWidth={1.5} />
          </>
        )}
      </svg>
      <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
        {formatter(zMin)} to {formatter(zMax)}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Data table view                                                     */
/* ------------------------------------------------------------------ */

interface PointTableProps {
  points: ScatterPoint[];
  xLabel: string;
  yLabel: string;
  zLabel?: string;
  unit: string;
  showGroup: boolean;
  open: boolean;
  xFormatter: (value: number) => string;
  yFormatter: (value: number) => string;
  zFormatter: (value: number) => string;
  onToggle: () => void;
  markShape?: MarkShape;
}

/**
 * Text equivalent of the plot: one row per mark.
 *
 * On a dense scatter this is often the only way to read an individual point at
 * all, since overlapping marks cannot each be hovered. It is also the relief
 * for light-mode fills that fall below 3:1 against the card.
 */
export function PointTable({
  points,
  xLabel,
  yLabel,
  zLabel,
  unit,
  showGroup,
  open,
  xFormatter,
  yFormatter,
  zFormatter,
  onToggle,
  markShape = "circle",
}: PointTableProps) {
  return (
    <div className="mt-3 border-t border-border pt-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-md px-1 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {open ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        {open ? "Hide data table" : "Show data table"}
      </button>

      {open && (
        <div className="mt-2 max-h-[280px] overflow-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b border-border">
                <th scope="col" className="py-1.5 pr-3 font-semibold text-muted-foreground">
                  Point
                </th>
                <th scope="col" className="py-1.5 pl-3 text-right font-semibold text-muted-foreground">
                  {xLabel}
                </th>
                <th scope="col" className="py-1.5 pl-3 text-right font-semibold text-muted-foreground">
                  {yLabel}
                </th>
                {zLabel && (
                  <th
                    scope="col"
                    className="py-1.5 pl-3 text-right font-semibold text-muted-foreground"
                  >
                    {zLabel}
                  </th>
                )}
                {showGroup && (
                  <th scope="col" className="py-1.5 pl-3 font-semibold text-muted-foreground">
                    Group
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {points.map((point, index) => (
                <tr
                  key={`${point.label}-${index}`}
                  className="border-b border-border/50 last:border-0"
                >
                  <th scope="row" className="py-1.5 pr-3 font-medium text-foreground">
                    <span className="flex items-start gap-2">
                      <span
                        aria-hidden="true"
                        className={cn(
                          "mt-[3px] size-2.5 shrink-0",
                          markShape === "diamond" ? "rotate-45 rounded-[2px]" : "rounded-full",
                        )}
                        style={{ backgroundColor: point.color }}
                      />
                      <span className="break-words">{point.label}</span>
                    </span>
                  </th>
                  <td className="py-1.5 pl-3 text-right font-bold tabular-nums text-foreground">
                    {xFormatter(point.x)}
                  </td>
                  <td className="py-1.5 pl-3 text-right font-bold tabular-nums text-foreground">
                    {yFormatter(point.y)}
                    {unit}
                  </td>
                  {zLabel && (
                    <td className="py-1.5 pl-3 text-right font-bold tabular-nums text-foreground">
                      {typeof point.z === "number" ? (
                        zFormatter(point.z)
                      ) : (
                        <span className="font-normal text-muted-foreground">-</span>
                      )}
                    </td>
                  )}
                  {showGroup && (
                    <td className="py-1.5 pl-3 text-muted-foreground">
                      <span className="break-words">{point.groupLabel}</span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
